import * as z from "zod";
import { userFacingError } from "../authorization";
import { zql } from "../generatedDrizzleSchema";
import { mutator } from "./mutator";

export const forkPersonalChatSchema = z.object({
  id: z.string(),
  chatId: z.string(),
  sourceChatId: z.string(),
  entryIds: z.record(z.string(), z.string()),
  attachmentIds: z.record(z.string(), z.string()),
});

export function forkedChatName(sourceName: string): string {
  return sourceName === "Untitled" ? "Untitled" : `Copy of ${sourceName}`;
}

export const forkPersonalChat = mutator({
  schema: forkPersonalChatSchema,
  resolve: {},
  permissions: {
    none: "personal chats are read and written only for ctx.userId",
  },
  fn: async ({ tx, ctx, args }) => {
    const { id, chatId, sourceChatId, entryIds, attachmentIds } = args;
    const { userId, tenantId } = ctx;

    const [source] = await tx.run(
      zql.personalChats.where("chatId", sourceChatId).where("userId", userId)
    );
    if (!source) throw userFacingError("Personal chat not found");

    const [sourceChat] = await tx.run(zql.chats.where("id", sourceChatId));
    if (!sourceChat) throw userFacingError("Chat not found");

    const now = Date.now();
    await tx.mutate.chats.insert({
      id: chatId,
      commitId: null,
      createdAt: now,
      lastChatEntryType: sourceChat.lastChatEntryType,
      respondingAt: null,
      awaitingAction: false,
      tenantId,
    });
    await tx.mutate.personalChats.insert({
      id,
      chatId,
      userId,
      name: forkedChatName(source.name),
      createdAt: now,
      closedAt: null,
      lastActiveAt: now,
      derivedWorkflowId: null,
      tenantId,
    });

    const entries = await tx.run(
      zql.chatEntries
        .where("chatId", sourceChatId)
        .orderBy("createdAt", "asc")
        .orderBy("id", "asc")
        .related("userMessage")
        .related("assistantMessage")
        .related("toolCall")
        .related("assistantReasoning")
        .related("systemMessage")
        .related("userAction")
        .related("recap")
        .related("attachments")
    );

    let previousEntryId: string | null = null;
    for (const entry of entries) {
      const newEntryId = entryIds[entry.id];
      if (entry.streaming || !newEntryId) continue;
      if (entry.recap && !entryIds[entry.recap.throughEntryId]) continue;

      await tx.mutate.chatEntries.insert({
        id: newEntryId,
        chatId,
        entryType: entry.entryType,
        createdAt: entry.createdAt,
        previousEntryId,
        streaming: false,
        tenantId,
      });
      previousEntryId = newEntryId;

      if (entry.userMessage) {
        await tx.mutate.userMessages.insert({
          entryId: newEntryId,
          userId: entry.userMessage.userId,
          text: entry.userMessage.text,
          isDictated: entry.userMessage.isDictated,
          planMode: entry.userMessage.planMode,
          effort: entry.userMessage.effort,
          actionKind: entry.userMessage.actionKind,
          tenantId,
        });
      }
      if (entry.assistantMessage) {
        await tx.mutate.assistantMessages.insert({
          entryId: newEntryId,
          text: entry.assistantMessage.text,
          tenantId,
        });
      }
      if (entry.toolCall) {
        await tx.mutate.toolCalls.insert({
          entryId: newEntryId,
          toolName: entry.toolCall.toolName,
          anthropicId: entry.toolCall.anthropicId,
          input: entry.toolCall.input,
          output: entry.toolCall.output,
          isError: entry.toolCall.isError,
          completedAt: entry.toolCall.completedAt,
          tenantId,
        });
      }
      if (entry.assistantReasoning) {
        await tx.mutate.assistantReasonings.insert({
          entryId: newEntryId,
          text: entry.assistantReasoning.text,
          signature: entry.assistantReasoning.signature,
          durationMs: entry.assistantReasoning.durationMs,
          tenantId,
        });
      }
      if (entry.systemMessage) {
        await tx.mutate.systemMessages.insert({
          entryId: newEntryId,
          text: entry.systemMessage.text,
          tenantId,
        });
      }
      if (entry.userAction) {
        await tx.mutate.userActions.insert({
          entryId: newEntryId,
          userId: entry.userAction.userId,
          actionType: entry.userAction.actionType,
          metadata: entry.userAction.metadata,
          tenantId,
        });
      }
      if (entry.recap) {
        await tx.mutate.recaps.insert({
          entryId: newEntryId,
          text: entry.recap.text,
          throughEntryId: entryIds[entry.recap.throughEntryId]!,
          tenantId,
        });
      }
      for (const attachment of entry.attachments) {
        const newAttachmentId = attachmentIds[attachment.id];
        if (!newAttachmentId) continue;
        await tx.mutate.attachments.insert({
          id: newAttachmentId,
          entryId: newEntryId,
          fileName: attachment.fileName,
          mediaType: attachment.mediaType,
          sizeBytes: attachment.sizeBytes,
          tenantId,
        });
      }
    }
  },
});
